import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Crown, MapPin, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LeadTable } from "@/components/LeadManagement";
import { LeadStatusBadge, fmtLeadDate } from "@/components/LeadStatus";

// iter203 — My Leads: only the caller's own Shoot Lead bookings. No money.
function LeadDetail({ bookingId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!bookingId) return;
    setD(null);
    api.get(`/team/me/leads/${bookingId}`).then((r) => setD(r.data)).catch((e) => { toast.error(e.response?.data?.detail || "Could not load booking"); onClose(); });
  }, [bookingId, onClose]);
  return (
    <Dialog open={!!bookingId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white" data-testid="my-lead-detail">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl" data-testid="my-lead-detail-name">{d?.couple_name || "Loading…"}</DialogTitle>
        </DialogHeader>
        {d && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 text-sm text-stone-600">
              <LeadStatusBadge status={d.lead_status} testid="my-lead-detail-status" />
              <span>{d.event}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{d.location || "—"}</span>
              <span className="text-stone-400">{d.event_days} day(s)</span>
            </div>
            {d.days.map((day, i) => (
              <div key={i} className="border border-stone-200 rounded-lg p-3" data-testid={`my-lead-day-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-stone-800">{fmtLeadDate(day.date)} · {day.event || d.event}</span>
                  <span className="text-xs text-stone-500 flex items-center gap-1" data-testid={`my-lead-day-calltime-${i}`}>
                    <Clock className="w-3 h-3" /> {day.call_time || "Call time TBD"}
                  </span>
                </div>
                {day.side && <div className="text-[11px] text-stone-400 mt-0.5">{day.side}</div>}
                <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
                  {day.crew.length === 0 && <li className="text-stone-400">No crew rows yet</li>}
                  {day.crew.map((c, j) => (
                    <li key={j} className="flex justify-between gap-2 bg-stone-50 rounded px-2 py-1" data-testid={`my-lead-day-${i}-crew-${j}`}>
                      <span className="text-stone-500">{c.role}</span>
                      <span className="font-medium text-stone-800">{c.name || "Unassigned"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const MyLeads = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");
  const close = useCallback(() => setOpen(""), []);
  useEffect(() => {
    api.get("/team/me/leads").then((r) => setData(r.data)).catch(() => setData({ leads: [], summary: null }));
  }, []);
  if (!data || !data.summary) return null;
  const s = data.summary;
  return (
    <section className="space-y-3" data-testid="my-leads-section">
      <header className="flex items-end justify-between flex-wrap gap-2">
        <h2 className="font-serif text-lg text-stone-800 flex items-center gap-2">
          <Crown className="w-4 h-4 text-rose-700" strokeWidth={1.8} /> My Leads
        </h2>
        <div className="flex gap-3 text-xs text-stone-500 font-mono">
          <span data-testid="my-leads-total">{s.total} handled (excl. cancelled)</span>
          <span data-testid="my-leads-active">{s.active} active</span>
          <span data-testid="my-leads-completed">{s.completed} completed</span>
          <span data-testid="my-leads-cancelled">{s.cancelled} cancelled</span>
        </div>
      </header>
      <LeadTable leads={data.leads} showLead={false} onRowClick={(l) => setOpen(l.booking_id)} testPrefix="my-leads" />
      <LeadDetail bookingId={open} onClose={close} />
    </section>
  );
};
